import type { Account, Edr, Infringement } from "@/types/domain";
import type { Dashboard } from "@/types/dashboard";
import type { IDataService } from "./dataService";

const MOCK_EDRS: Edr[] = [
  { id: "edr-1", label: "Anti-Piracy", status: "active", description: "", last_heartbeat: null },
  { id: "edr-2", label: "CDN Analytics", status: "active", description: "", last_heartbeat: null },
];

// Seeded pseudo-random — deterministic across reloads so IDs/values stay stable.
function seededRand(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

function generateInfringements(count = 40): Infringement[] {
  const rand = seededRand(42);
  const pick = <T>(arr: readonly T[]): T => arr[Math.floor(rand() * arr.length)];
  const randIp = () => [1, 2, 3, 4].map(() => Math.floor(rand() * 254) + 1).join(".");
  const randConf = () => Math.round((0.5 + rand() * 0.5) * 100) / 100;

  const severities = ["critical", "high", "medium", "low", null] as const;
  const statuses = ["open", "open", "open", "in_progress", "resolved"] as const;
  const actions = [null, null, null, "block", "ignore"] as const;
  const actionResults: Record<string, string> = { block: "blocked", ignore: "ignored" };
  const events = [
    null,
    null,
    "UEFA CL Final",
    "Premier League GW32",
    "MotoGP Jerez",
    "NBA Playoffs",
  ] as const;
  const services = ["PirateStream", "StreamFree", "UltraHD.to", "LiveKing"];
  const cdns = ["Akamai", "Cloudflare", "Fastly", "CloudFront"];
  const channels = ["Sports HD", "Cinema 4K", "News 24", "Entertainment Plus"];

  const seeds: Array<{
    type: Infringement["infringement_type"];
    edr: string;
    makeDetails: (ip: string, rand: () => number) => Infringement["details"];
  }> = [
    {
      type: "restreaming",
      edr: "edr-1",
      makeDetails: (ip) => ({
        ip,
        fqdn: `stream${Math.floor(rand() * 99)}.pirate.tv`,
        channel: pick(channels),
        pirate_service: pick(services),
      }),
    },
    {
      type: "theft_for_restreaming",
      edr: "edr-2",
      makeDetails: (ip) => ({
        ips: [ip, randIp()],
        cdns: [pick(cdns)],
        tokens: [`tok_${Math.floor(rand() * 0xfffff).toString(16)}`],
        accounts: [`user${Math.floor(rand() * 999)}@example.com`],
        channels: [pick(channels)],
      }),
    },
    {
      type: "cdn_leeching",
      edr: "edr-2",
      makeDetails: (ip) => ({
        ips: [ip],
        cdns: [pick(cdns), pick(cdns)],
        tokens: [
          `tok_${Math.floor(rand() * 0xfffff).toString(16)}`,
          `tok_${Math.floor(rand() * 0xfffff).toString(16)}`,
        ],
        accounts: [],
        channels: [pick(channels)],
      }),
    },
  ];

  return Array.from({ length: count }, (_, i) => {
    const seed = seeds[i % seeds.length];
    const ip = randIp();
    const action: string | null = pick(actions);
    const action_result: string | null = action ? (actionResults[action] ?? null) : null;
    const ago = Math.floor(rand() * 7 * 24 * 3600000);
    return {
      id: `inf-${i + 1}`,
      tenant_id: "default",
      external_id: rand() > 0.5 ? ip : null,
      edr_id: seed.edr,
      infringement_type: seed.type,
      confidence: randConf(),
      event: pick(events),
      severity: pick(severities),
      status: pick(statuses),
      action,
      action_result,
      action_history: action
        ? [
            {
              action,
              timestamp: new Date(Date.now() - ago / 2).toISOString(),
              action_result: action_result ?? undefined,
            },
          ]
        : [],
      detected: new Date(Date.now() - ago).toISOString(),
      lastseen: new Date(Date.now() - Math.floor(rand() * ago)).toISOString(),
      details: seed.makeDetails(ip, rand),
    } satisfies Infringement;
  });
}

const MOCK_INFRINGEMENTS = generateInfringements(40);

const MOCK_DASHBOARDS: Dashboard[] = [
  {
    id: "mock-dash-1",
    name: "Mock Dashboard",
    slug: "mock-dashboard",
    description: "Local dev dashboard",
    sources: [],
    status: "available",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    sections: [
      {
        id: "sec-1",
        name: "Overview",
        order: 0,
        collapsed: false,
        placements: [
          {
            widgetId: "kpi_summary",
            instanceId: "kpi_summary_1",
            layout: { i: "kpi_summary_1", x: 0, y: 0, w: 3, h: 4 },
          },
          {
            widgetId: "chart_infringement_types",
            instanceId: "chart_infringement_types_1",
            layout: { i: "chart_infringement_types_1", x: 3, y: 0, w: 4, h: 4 },
          },
          {
            widgetId: "chart_top_pirates",
            instanceId: "chart_top_pirates_1",
            layout: { i: "chart_top_pirates_1", x: 7, y: 0, w: 5, h: 4 },
          },
        ],
      },
      {
        id: "sec-2",
        name: "Infringements",
        order: 1,
        collapsed: false,
        placements: [
          {
            widgetId: "table_infringements",
            instanceId: "table_infringements_1",
            layout: { i: "table_infringements_1", x: 0, y: 0, w: 12, h: 8 },
          },
        ],
      },
    ],
  },
];

export class MockDataService implements IDataService {
  private infringements = structuredClone(MOCK_INFRINGEMENTS);

  async getInfringements(params?: {
    lastseen_from?: string;
    lastseen_to?: string;
  }): Promise<{ infringements: Infringement[]; count: number }> {
    let items = this.infringements;
    if (params?.lastseen_from) {
      items = items.filter(i => new Date(i.lastseen) >= new Date(params.lastseen_from!));
    }
    if (params?.lastseen_to) {
      items = items.filter(i => new Date(i.lastseen) <= new Date(params.lastseen_to!));
    }
    return { infringements: items, count: items.length };
  }
  async getInfringement(id: string): Promise<Infringement> {
    return this.infringements.find((i) => i.id === id)!;
  }
  async triggerInfringementAction(id: string, action: "block" | "ignore"): Promise<void> {
    this.infringements = this.infringements.map((i) =>
      i.id === id ? { ...i, action, action_result: action === "ignore" ? "ignored" : null } : i,
    );
  }
  async getAccount(): Promise<Account> {
    return {
      account_id: "mock-user",
      tenant: "default",
      roles: ["admin"],
      settings: {},
      created_at: null,
      updated_at: null,
    };
  }
  async getEdrs(): Promise<Edr[]> {
    return MOCK_EDRS;
  }
  async getDashboards(): Promise<Dashboard[]> {
    return MOCK_DASHBOARDS;
  }
  async getDashboard(id: string): Promise<Dashboard> {
    const d = MOCK_DASHBOARDS.find((d) => d.id === id);
    if (!d) throw new Error(`Mock: dashboard ${id} not found`);
    return d;
  }
  async createDashboard(config: Dashboard): Promise<Dashboard> {
    return { ...config, id: "mock-dash" };
  }
  async updateDashboard(_id: string, config: Dashboard): Promise<Dashboard> {
    return config;
  }
  async deleteDashboard(_id: string): Promise<void> {}
}
